import { Component, OnInit, OnDestroy } from '@angular/core';
import { combineLatest, Subject } from 'rxjs';
import { filter, map, takeUntil } from 'rxjs/operators';

import { AwardsService, AwardProgramInfo } from 'services/awards/awards.service';
import { ApplicationService, SchoolGroupInfo, SchoolInfo } from 'services/application/application.service';
import { ErrorService } from 'services/error/error.service';
import { SettingsService } from 'services/settings/settings.service';
import { UserService } from 'services/user/user.service';

@Component({
    selector: 'app-school',
    styleUrls: ['school.component.scss'],
    templateUrl: 'school.component.html',
})

export class SchoolSelectionComponent implements OnInit, OnDestroy {
    private _subrelease: Subject<void> = new Subject<void>();

    AllSchools: SchoolInfo[];
    AvailableSchoolGroups: SchoolGroupInfo[];
	AvailableSchools: SchoolInfo[];
	AvailableYears: string[];
    AwardInfo: AwardProgramInfo;
    CurrentSchool: SchoolInfo;
    CurrentSchoolYear: string;
	TentativeSchool: SchoolInfo;
	TentativeYear: string;
	SchoolYearByDate: string;
    ViewReady: boolean;

    constructor(
        private appSvc: ApplicationService,
        private awardSvc: AwardsService,
        private errorSvc: ErrorService,
        private settingsSvc: SettingsService,
        private userSvc: UserService
    ) {}

    ngOnDestroy() {
        this._subrelease.next();
        this._subrelease.complete();
    }

    ngOnInit() {
        const self = this;

        self.AvailableSchools = null;
        self.AwardInfo = null;
        self.CurrentSchool = null;
		self.CurrentSchoolYear = null;
        self.TentativeSchool = null;
		self.ViewReady = false;
		self.SchoolYearByDate = this.settingsSvc.SchoolYearByDate;

        // Set up a subscription on the current award program, so we know which schools are available (if that's a thing),
        // and also on the current school year.
        combineLatest(
            self.awardSvc.CurrentAwardsSub,
			self.settingsSvc.CurrentSchoolYearSubject
        ).pipe(
            takeUntil(self._subrelease),
            map(_pck => {
                return {
                    Awards: _pck[0],
                    Year: _pck[1],
                };
            }),
            filter(pck => pck && !!pck.Awards)
        ).subscribe(packet => {
            // Get the current award program
            self.AwardInfo = packet.Awards;
            self.CurrentSchoolYear = packet.Year;

            // Load everything we need for this page
			const allSchoolsObs = self.appSvc.GetAllSchools(self.CurrentSchoolYear);
            const allSchoolGrpsObs = self.appSvc.GetAllSchoolGroups(self.AwardInfo.AwardID, self.CurrentSchoolYear);
            const curSchoolObs = self.settingsSvc.CurrentSchoolSubject;
			const nomTypesObs = self.appSvc.GetNomineeTypes();
			const schoolEligibilityObs = self.appSvc.GetSchoolEligibility(self.AwardInfo.AwardID, self.CurrentSchoolYear);
			const allSchoolYearsObs = self.appSvc.GetAllSchoolYears(self.AwardInfo.AwardID, self.SchoolYearByDate);

			combineLatest(allSchoolsObs, allSchoolGrpsObs, curSchoolObs, nomTypesObs, schoolEligibilityObs, allSchoolYearsObs).pipe(
                takeUntil(self._subrelease),
                map(_spck => {
                    return {
                        Schools: _spck[0],
                        Groups: _spck[1],
                        Currentschool: _spck[2],
                        NomTypes: _spck[3],
						EligibilityList: _spck[4],
						AllYears: _spck[5]
                    };
                }),
                filter(p => !!p && !!p.Schools && !!p.Groups)
            ).subscribe(spacket => {
                self.AllSchools = spacket.Schools || [];
                self.AvailableSchoolGroups = spacket.Groups || [];
                self.CurrentSchool = spacket.Currentschool;
                let personType = spacket.NomTypes.filter(t => t.AwardID === self.AwardInfo.AwardID)[0].PersonType;
				let eligibleSchools = spacket.EligibilityList;
				self.AvailableYears = spacket.AllYears;

                // Parse the schools by iterating all schools. In instances where the school number is part of a
                // known school group, skip adding that school. Once we're done, add in the school groups, and then
                // sort by the name of the school/group lead.
                let schools = <SchoolInfo[]>[];

                self.AllSchools.forEach(sch => {
                    const grp = self.AvailableSchoolGroups.find(
                        item => {
                            if (item.LeadLocationNumber === sch.SchoolNumber) {
                                return true;
                            }
                            if (item.Schools) {
                                const sgrp = item.Schools.find(sitem => sitem.LocationNumber === sch.SchoolNumber);
                                if (sgrp) {
                                    return true;
                                }
                            }
                            return false;
                        }
                    );

                    if (!grp) {
                        schools.push(sch);
                    }
                });

                // Now we can also add in the school groups by their leader
                self.AvailableSchoolGroups.forEach(sch => {
					let lead = self.AllSchools.find(s => s.SchoolNumber === sch.LeadLocationNumber);

					let nums: string[] = [];
					for (let s of sch.Schools) {
						nums.push(s.LocationNumber);
					}

                    schools.push(<SchoolInfo>{
                        SchoolName: sch.Name,
                        SchoolNumber: sch.LeadLocationNumber,
                        IsElementary: ((lead != null) ? lead.IsElementary : false),
                        IsMiddle: ((lead != null) ? lead.IsMiddle : false),
						IsSenior: ((lead != null) ? lead.IsSenior : false),
						SchoolNumberList: nums.join(', '),
						InSchoolGroup: true
                    });
                });

                // If personType restricts schools, do that here
                if (personType === 'STUDENTELEM') {
                    schools = schools.filter(s => s.IsElementary === true);
                }
                else if (personType === 'STUDENTMIDDLE') {
                    schools = schools.filter(s => s.IsMiddle === true);
                }
                else if (personType === 'STUDENTHIGH') {
                    schools = schools.filter(s => s.IsSenior === true);
                }
                else if (personType === 'STUDENTMIDDLEHIGH') {
                    schools = schools.filter(s => s.IsMiddle === true || s.IsSenior === true || s.IsAdult === true);
                }
                else if (personType === 'STUDENTELEMMIDDLE') {
                    schools = schools.filter(s => s.IsElementary === true || s.IsMiddle === true);
                }

                let eligSchools: SchoolInfo[] = [];

                //weed out non eligible schools based on Award
                for (let s of schools) {
                    if (eligibleSchools.findIndex(e => e.Display === s.SchoolNumber && e.Value === self.AwardInfo.AwardID) > -1) {
                        eligSchools.push(s);
                    }
                }

                // Lastly, sort and store
				self.AvailableSchools = eligSchools.sort((a, b) => a.SchoolName.localeCompare(b.SchoolName));

				// Sync the TentativeSchool to the school object provided.
				self.TentativeSchool = !self.CurrentSchool ? null : (self.AvailableSchools || []).find(item =>
					item.SchoolNumber === self.CurrentSchool.SchoolNumber
				);

				//Sync tentativeYear
				self.TentativeYear = self.CurrentSchoolYear;

                // Release the view
                self.ViewReady = true;
            }, error => self.errorSvc.HandleServiceError(error));
        });
    }

    SetCurrentSchool() {
        const self = this;
        let user = self.userSvc.User;

        if (!self.TentativeSchool) {
            return;
        }

        // Trigger the update so it ripples application wide
        self.settingsSvc.SetCurrentSchool(self.TentativeSchool);
        self.appSvc.GetNominationsByWorkLocation(user.AuthToken, self.TentativeSchool.SchoolNumber, user.AwardID, self.CurrentSchoolYear).pipe(takeUntil(self._subrelease)).subscribe();
	}

	SetCurrentSchoolYear() {
		let user = this.userSvc.User;

		if (!this.TentativeYear) {
			return;
		}
		//set schoolYear
		this.settingsSvc.SetSchoolYear(this.TentativeYear);
		//reset selected school
		this.settingsSvc.SetCurrentSchool(null);
		this.appSvc.GetNominationsByWorkLocation(user.AuthToken, this.TentativeSchool.SchoolNumber, user.AwardID, this.CurrentSchoolYear).pipe(takeUntil(this._subrelease)).subscribe();
	}
}
